import { FunctionComponent, useState, useRef } from 'react'
import ReactQuill from 'react-quill-new'
import { quillModules, getSemanticHtml } from 'Frontend/scripts/quillModules'
import { Button, Form } from 'react-bootstrap'
import { deleteWorkroomPostComment, updateWorkroomPostComment } from 'Frontend/scripts/services/workrooms.service'
import { convertDateAndTime } from 'Frontend/scripts/convertDates'
import { useForm, Controller } from 'react-hook-form'
import Dialog from 'Features/Dialog/Dialog'
import { IWorkroomComment } from 'Frontend/scripts/models/workrooms.model'
import { IAntiforgeryToken } from 'Frontend/scripts/models/general.model'
import { getAntiforgeryToken } from 'Frontend/scripts/services/general.service'
import { useTranslation } from 'react-i18next'

interface WorkroomPostCommentProps {
  workroomId: number
  postId: number
  comment: IWorkroomComment
  callback: () => void
}
interface WorkRoomPostCommentForm {
  mainbody: string
}
const WorkroomPostComment: FunctionComponent<WorkroomPostCommentProps> = ({
  workroomId,
  postId,
  comment,
  callback,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [mainbody, setMainbody] = useState<string>(comment.text)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const quillRef = useRef<ReactQuill | null>(null)
  const { t } = useTranslation()

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<WorkRoomPostCommentForm>({
    defaultValues: {
      mainbody: comment.text,
    },
  })

  const submitAnswer = () => {
    const mainBodyWithCorrectHTML = getSemanticHtml(quillRef)
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      updateWorkroomPostComment(token.csrfToken, workroomId, postId, comment.id, mainBodyWithCorrectHTML).then(() => {
        setIsEditing(false)
        setMainbody(mainBodyWithCorrectHTML)
      })
    })
  }

  const abortEditing = () => {
    setMainbody(mainbody)
    setIsEditing(false)
  }

  const deletePost = () => {
    getAntiforgeryToken().then((token: IAntiforgeryToken) => {
      deleteWorkroomPostComment(token.csrfToken, workroomId, postId, comment.id).then(() => {
        setIsDialogOpen(false)
        callback()
      })
    })
  }

  const openDialog = () => {
    setIsDialogOpen(true)
  }

  const createMarkup = (body: string) => {
    return { __html: body }
  }

  if (!isEditing) {
    return (
      <div className="livstecknet-thread__answer-area">
        <div dangerouslySetInnerHTML={createMarkup(mainbody)}></div>
        <div className="d-flex gap-2">
          {comment.createdByUser == null ? (
            <div className="livstecknet-thread__profile-initial-letters">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <use xlinkHref="#icon-profile" />
              </svg>
            </div>
          ) : comment.createdByUser.profileImageUrl == null ? (
            <div className="livstecknet-thread__profile-initial-letters">
              {comment.createdByUser?.firstname.charAt(0)}
              {comment.createdByUser?.lastname.charAt(0)}
            </div>
          ) : (
            <img
              className="workrooms__profile-image"
              src={comment.createdByUser.profileImageUrl}
              alt={`${t('image-of')} ${comment.createdByUser?.firstname} ${comment.createdByUser?.lastname}`}
            />
          )}
          <div className="align-self-center">
            {comment.createdByUser != null && (
              <a href={comment.createdByUser.viewProfileUrl} className="livstecknet-thread__author">
                {comment.createdByUser?.firstname}&nbsp;
                {comment.createdByUser?.lastname}
              </a>
            )}
            <p className="livstecknet-thread__time mb-0">{convertDateAndTime(comment.created)}</p>
          </div>
        </div>
        <div className="d-flex flex-shrink-0 gap-2 justify-content-end livstecknet-thread__handle-thread">
          {comment.currentUserHasAccessToEdit && (
            <Button
              variant="outline-secondary"
              className="gap-2 livstecknet-thread__edit-question"
              onClick={() => setIsEditing(true)}
            >
              <span className="fa fa-pencil"></span>
              {t('edit')}
            </Button>
          )}
          {comment.currentUserHasAccessToDelete && (
            <Button variant="dark" className="gap-2 livstecknet-thread__edit-question" onClick={() => openDialog()}>
              <span className="fa fa-trash-o"></span>
              {t('delete')}
            </Button>
          )}
        </div>
        <Dialog
          isOpen={isDialogOpen}
          verificationText={t('verification-delete-comment')}
          submitText={t('yes-delete-post')}
          abortText={t('abort')}
          buttonVariant="dark"
          callbackSubmit={() => deletePost()}
          callbackClose={() => setIsDialogOpen(false)}
        />
      </div>
    )
  }

  return (
    <div className="livstecknet-thread__answer-area">
      <div>
        <Form noValidate onSubmit={handleSubmit(submitAnswer)} className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label htmlFor="inputCommentMainbody">{t('text')}</Form.Label>
            <Controller
              name="mainbody"
              control={control}
              rules={{
                validate: (value) => {
                  const strippedText = value?.replace(/<(.|\n)*?>/g, '').trim()
                  return strippedText.length > 0 || t('error-description')
                },
              }}
              render={({ field }) => (
                <ReactQuill
                  {...field}
                  ref={quillRef}
                  id="inputCommentMainbody"
                  className="notice__quill-editor"
                  theme="snow"
                  modules={quillModules}
                  onChange={(value) => {
                    setValue('mainbody', value, { shouldValidate: true })
                    trigger('mainbody')
                  }}
                />
              )}
            />
            {errors.mainbody && <Form.Text className="text-danger">{errors.mainbody.message}</Form.Text>}
          </Form.Group>

          <div className="d-flex gap-3">
            <Button variant="secondary" type="submit">
              {t('save-changes')}
            </Button>
            <Button variant="outline-secondary" type="button" onClick={() => abortEditing()}>
              {t('abort')}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default WorkroomPostComment
